import { HomeDashboard } from "@/features/home_dashboard";
import { defaultAbcRiskBandsConfig, defaultInputValues } from "@/features/risk_bands";
import { hazards, HubTheme } from "../../types";

const defaultTheme: HubTheme = {
  meta: {
    name: "Hub",
    aesthetics: {
      attribution: "none",
      authorisation: {
        signUp: {},
      },
    },
    config: {
      featureSet: {
        comms: {
          feedback: {
            enabled: false,
          },
          support: {
            enabled: false,
          },
          emailSupport: {
            enabled: false,
            email: "support@xdi.systems",
          },
        },
        docs: {
          accountManagementTutorial: {
            enabled: false,
          },
          contextualElementTutorials: {
            enabled: false,
            overrides: {},
          },
          knowledgeBase: {
            glossary: {
              enabled: false,
            },
            archetypes: {
              enabled: false,
            },
            method: {
              enabled: true,
            },
            modelling: { enabled: false },
            geocoding: {
              enabled: true,
              embedPostId: "db825135-ec59-4332-a598-8c68de678109",
            },
            changeLog: {
              enabled: true,
            },
            faqs: {
              enabled: true,
            },
            dataExplorer: { enabled: false },
          },
          legal: {
            privacyPolicy: "https://xdi.systems/privacy-policy",
            termsOfService: "https://xdi.systems/terms-of-service",
          },
          tutorialsAndTips: {
            enabled: true,
            url: "/guide/_",
          },
          toolPageTutorials: {
            enabled: false,
          },
        },
        settings: {
          enabled: false,
        },
        tools: [
          /* These must be set up within each theme for optimal tree-shaking. */
        ],
        tracking: {
          hubspot: {
            enabled: false,
          },
          plausible: {
            enabled: false,
          },
        },
        widgets: {
          allowDocument360Widget: false,
        },
      },
      geocoding: {
        allowCoordinatesInQuickBatchScreen: true,
        allowReordering: false,
        confidenceThreshold: 65,
        coordinateRestrictions: {
          showBoundsOnMap: false,
        },
        defaultMapCentre: {
          lat: 4.241468,
          lng: 11.425781,
        },
        defaultMapZoom: 2,
        defaultOrder: ["google"],
        locationRestrictions: {},
        minLengthForSuggestions: 5,
        guidelineDocumentUrl:
          "https://portal.document360.io/6b551567-9816-4779-b18e-630e9d63ea4b/document/v2/view/45399faf-c596-47ad-a5bd-ec479f97f04f",
      },
      inputConstraints: {
        addressLength: { min: 1, max: 200 },
        buildYear: { min: 1600, max: 2100 },
        // floor below 0 does not work with structural analysis; see products
        floorHeight: { min: 0, max: 10000 },
      },
      locale: {
        defaultInputValues: defaultInputValues.aud.residential,
        defaultLanguage: "en-AU",
        supportedLanguages: ["en-AU"],
      },
      pages: {
        collections: {
          enabled: false,
        },
        home: HomeDashboard,
      },
      riskBands: defaultAbcRiskBandsConfig,
      hazards,
      defaultMetadata: {
        defences: {
          flood_riverine: "defended",
          flood_surfacewater: "defended",
          coastal_inundation: "defended",
          cyclone_storm_surge: "defended",
        },
        defencesDisabled: {
          flood_riverine: "undefended",
          flood_surfacewater: "undefended",
          coastal_inundation: "undefended",
          cyclone_storm_surge: "undefended",
        },
        anomalyManagementRp: {
          flood_riverine: 20,
          flood_surfacewater: 20,
          coastal_inundation: 20,
          cyclone_storm_surge: 20,
        },
        anomalyManagementRpDisabled: {
          flood_riverine: null,
          flood_surfacewater: null,
          coastal_inundation: null,
          cyclone_storm_surge: null,
        },
      },
      portfolioExport: {
        assetLevelDeepDive: {
          outputTypes: ["small", "smallResilience", "exposures", "screen", "tall"],
        },
        largeBank: {
          minAddresses: 0,
        },
        maxAddresses: 20000,
        templates: {
          hk: {
            address: "8 Finance Street, Central",
            centre: {
              lat: 22.2855,
              lng: 114.1592,
            },
            defaultSettings: {
              elevation: 6.139,
              heatThresholdReturnPeriod: 100,
              heatThresholdReturnFrequency: 0.01,
              heatThresholdTemperature: 34.6,
              windThresholdReturnPeriod: 100,
              windThresholdReturnFrequency: 0.01,
              tcwThresholdReturnPeriod: 100,
              tcwThresholdReturnFrequency: 0.01,
              tcwThresholdWindSpeed: 51,
            },
          },
          global: {
            address: "385 Bourke St, Melbourne VIC 3000",
            centre: {
              lat: -37.8148594,
              lng: 144.9629097,
            },
            defaultSettings: {
              elevation: 9,
              heatThresholdReturnPeriod: 100,
              heatThresholdReturnFrequency: 0.01,
              heatThresholdTemperature: 45.4,
              windThresholdReturnPeriod: 100,
              windThresholdReturnFrequency: 0.01,
              tcwThresholdReturnPeriod: 100,
              tcwThresholdReturnFrequency: 0.01,
              tcwThresholdWindSpeed: 51,
            },
          },
        },
      },
    },
  },
  antd: {
    token: {
      // Output tokens, so a var() is safe here and flips with the ramp.
      colorTextDescription: "var(--color-gray-500)",
      // Seed token: antd derives colorLinkHover/Active from it, so it must stay
      // literal hex. The dark variant lives in antdDark below.
      colorLink: "#136be5",
      linkFocusDecoration: "underline",
    },
    components: {
      Layout: {
        bodyBg: "transparent",
        headerBg: "var(--color-gray-50)",
        padding: 0,
      },
      Menu: {
        itemBg: "transparent",
        itemSelectedColor: "var(--color-gray-800)",
        lineWidth: 0,
      },
      Input: {
        borderRadius: 8,
      },
      Select: {
        borderRadius: 8,
      },
      Button: {
        borderRadius: 8,
      },
    },
  },
  antdDark: {
    token: {
      // #136be5 is only 3.74:1 on the dark surface; this is the same hue raised
      // until it clears 4.5:1. See src/styles/accent.ts for the rule.
      colorLink: "#387edf",
    },
  },
  translationOverrides: {
    "en-AU": {
      terms: {
        scenarios: {
          rcp26: {
            name: "rcp26",
            long: "XDI RCP 2.6 / SSP1-2.6 aligns with NGFS Net Zero 2050",
            medium: "RCP 2.6 / SSP1-2.6 (Net Zero 2050)",
            short: "RCP2.6/SSP1-2.6",
          },
          rcp45: {
            name: "rcp45",
            long: "XDI RCP 4.5 / SSP2-4.5 aligns with NGFS Delayed Transition",
            medium: "RCP 4.5 / SSP2-4.5 (Delayed Transition)",
            short: "RCP4.5/SSP2-4.5",
          },
          rcp60: {
            name: "rcp60",
            long: "XDI RCP 6.0 aligns with NGFS Current Policies (median)",
            medium: "RCP 6.0 (Current Policies Median Range)",
            short: "RCP6.0",
          },
          rcp85: {
            name: "rcp85",
            long: "XDI RCP 8.5 / SSP5-8.5 aligns with NGFS Current Policies (high range)",
            medium: "RCP 8.5 / SSP5-8.5 (Current Policies High Range)",
            short: "RCP8.5/SSP5-8.5",
          },
        },
      },
    },
  },
};

export default defaultTheme;
